import { Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories, featuredComicIds, popularComicIds, latestComicIds } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

const featuredComics = featuredComicIds.map((id) => comics.find((c) => c.id === id)).filter(Boolean) as Comic[];
const popularComics = popularComicIds.map((id) => comics.find((c) => c.id === id)).filter(Boolean) as Comic[];
const latestComics = latestComicIds.map((id) => comics.find((c) => c.id === id)).filter(Boolean) as Comic[];

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: '韩漫吧',
      url: 'https://hanmanba.com',
      description: '韩漫吧致力于推荐好看韩国漫画，提供免费漫画大全和下拉式在线阅读服务。',
      sameAs: ['https://weibo.com/hanmanba'],
    },
    {
      '@type': 'WebSite',
      name: '韩漫吧',
      url: 'https://hanmanba.com',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://hanmanba.com/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'WebPage',
      name: '韩漫吧 - 首页',
      url: 'https://hanmanba.com',
      isPartOf: { '@type': 'WebSite', name: '韩漫吧' },
    },
  ],
};

const stats = [
  { value: '2000+', label: '漫画资源', icon: 'ri-book-2-line' },
  { value: '50万+', label: '注册用户', icon: 'ri-user-star-line' },
  { value: '99.9%', label: '服务可用', icon: 'ri-shield-star-line' },
  { value: '每日更新', label: '持续上新', icon: 'ri-refresh-line' },
];

export default function HomePage() {
  return (
    <>
      <SEOMeta
        title="韩漫吧 - 免费漫画大全 - 下拉式韩漫在线阅读 - 好看的韩国漫画推荐"
        description="韩漫吧致力于推荐好看韩国漫画，提供免费漫画大全和下拉式在线阅读服务，氛围轻松，是漫画同好分享与发现心仪作品的角落。海量韩漫资源，每日更新，支持手机和电脑在线阅读。"
        keywords="韩漫吧,免费漫画大全,下拉式韩漫在线阅读,好看的韩国漫画推荐,韩国漫画,韩漫,免费漫画,在线阅读"
        canonicalUrl="https://hanmanba.com"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20and%20artistic%20Korean%20manhwa%20webtoon%20style%20illustration%20featuring%20floating%20comic%20panels%20and%20speech%20bubbles%20in%20soft%20pastel%20colors%20coral%20pink%20warm%20orange%20and%20cream%20tones%20with%20scattered%20stars%20and%20sparkles%20dreamy%20atmospheric%20reading%20theme%20no%20text&width=1600&height=700&seq=hero-bg-2026&orientation=landscape"
            alt="韩漫吧 - 免费韩国漫画在线阅读平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
            title="韩漫吧 - 免费韩国漫画在线阅读平台"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-white mb-4 leading-tight">
              发现你爱的<span className="text-accent-400">韩国漫画</span>
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              海量免费韩漫资源，下拉式流畅阅读体验。每日更新热门漫画，在这里找到属于你的那一部。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/all"
                className="px-8 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/30"
              >
                <i className="ri-compass-3-line mr-2"></i>开始探索
              </Link>
              <Link
                to="/category/romance"
                className="px-8 py-3 rounded-full bg-white/15 backdrop-blur-sm text-white font-medium text-sm hover:bg-white/25 transition-all duration-200 whitespace-nowrap cursor-pointer border border-white/20"
              >
                <i className="ri-heart-line mr-2"></i>热门恋爱
              </Link>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="relative -mt-10 z-20 max-w-4xl mx-auto px-4 md:px-6">
          <div className="bg-background-50 rounded-2xl grid grid-cols-2 md:grid-cols-4 divide-x divide-background-200/70 overflow-hidden">
            {stats.map((stat) => (
              <div key={stat.label} className="flex flex-col items-center justify-center py-5 px-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 mb-2">
                  <i className={`${stat.icon} text-primary-600 text-sm`}></i>
                </div>
                <span className="font-heading text-xl md:text-2xl text-foreground-900">{stat.value}</span>
                <span className="text-xs text-foreground-500 mt-0.5">{stat.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pt-16 pb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900">
              漫画分类
            </h2>
            <Link to="/category/all" className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
              查看全部 <i className="ri-arrow-right-line ml-1 text-xs"></i>
            </Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.filter((c) => c.slug !== 'all').map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-100 hover:text-primary-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Featured Comics */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900">
              <i className="ri-fire-line text-accent-500 mr-2"></i>精选推荐
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {featuredComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} size="large" />
            ))}
          </div>
        </section>

        {/* Popular Comics */}
        <section className="bg-background-100/70 py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900">
                <i className="ri-trophy-line text-accent-500 mr-2"></i>人气排行
              </h2>
              <Link to="/category/all" className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
                更多 <i className="ri-arrow-right-line ml-1 text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-4">
              {popularComics.map((comic, index) => (
                <div key={comic.id} className="relative">
                  <div className="absolute -top-1 -left-1 w-7 h-7 flex items-center justify-center rounded-full bg-accent-500 text-white text-xs font-bold z-10 shadow-sm">
                    {index + 1}
                  </div>
                  <ComicCard comic={comic} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900">
              <i className="ri-timer-flash-line text-accent-500 mr-2"></i>最近更新
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {latestComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-background-100 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              浏览全部漫画 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative w-full py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20soft%20warm%20gradient%20abstract%20background%20in%20coral%20pink%20and%20peach%20tones%20with%20floating%20geometric%20shapes%20and%20subtle%20texture%20dreamy%20atmospheric%20cozy%20reading%20vibe%20minimal%20clean%20design&width=1600&height=500&seq=cta-bg-2026&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-500/80 to-accent-500/70"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center px-4">
            <h2 className="font-heading text-2xl md:text-3xl text-white mb-3">
              开始你的韩漫之旅
            </h2>
            <p className="text-white/80 text-sm md:text-base mb-7 leading-relaxed">
              数千部精选韩国漫画，随时随地免费阅读。加入韩漫吧，发现更多精彩作品。
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-primary-600 font-semibold text-sm hover:bg-white/95 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-lg"
            >
              立即探索 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}